import React, { useState, useEffect, useCallback } from 'react';
import { auth, apiFetch } from '../apiClient';
import { Profile } from '../hooks/useAuth';
import { StorefrontSettings } from '../components/Provider/StorefrontSettings';
import { ServiceCatalog } from '../components/Provider/ServiceCatalog';
import { BookingManagement } from '../components/Provider/BookingManagement';
import { EarningsManagement } from '../components/Provider/EarningsManagement';
import { InvitationManagement } from '../components/Admin/InvitationManagement';
import { PayoutManagement } from '../components/Admin/PayoutManagement';
import { SystemSettings } from '../components/Admin/SystemSettings';
import { Card } from '../components/ui/Card';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import {
  LayoutDashboard,
  LogOut,
  Settings,
  Calendar,
  ShoppingBag,
  Globe,
  UserPlus,
  Clock,
  Wallet,
  Menu,
  ChevronLeft,
  ArrowUpRight,
  Copy,
  Check,
  ExternalLink,
  LucideIcon,
} from 'lucide-react';

type TabId = 'overview' | 'storefront' | 'services' | 'bookings' | 'invites' | 'earnings' | 'payouts' | 'settings';

interface NavItem {
  id: TabId;
  label: string;
  icon: LucideIcon;
}

const PROVIDER_NAV: NavItem[] = [
  { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'storefront', label: 'Storefront', icon: Globe },
  { id: 'services', label: 'Services', icon: ShoppingBag },
  { id: 'bookings', label: 'Bookings', icon: Calendar },
  { id: 'earnings', label: 'Earnings', icon: Wallet },
];

const ADMIN_NAV: NavItem[] = [
  { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'invites', label: 'Invites', icon: UserPlus },
  { id: 'payouts', label: 'Payouts', icon: ArrowUpRight },
  { id: 'settings', label: 'System', icon: Settings },
];

const NavButton: React.FC<{
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}> = ({ item, active, collapsed, onClick }) => {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      title={collapsed ? item.label : ''}
      className={`w-full flex items-center ${collapsed ? 'justify-center' : 'gap-4 px-5'} py-3.5 rounded-sm label-caps transition-colors ${
        active
          ? 'text-accent bg-white/10 border-l-2 border-accent'
          : 'text-slate-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'
      }`}
    >
      <Icon size={18} className="shrink-0" />
      {!collapsed && <span>{item.label}</span>}
    </button>
  );
};

const MetricCard: React.FC<{ label: string; value: string; hint?: string; onClick?: () => void }> = ({
  label, value, hint, onClick,
}) => (
  <Card accent className={`p-6 ${onClick ? 'cursor-pointer hover:border-line-strong transition-colors' : ''}`} onClick={onClick}>
    <p className="label-caps text-ink-faint">{label}</p>
    <p className="display text-3xl text-ink mt-2">{value}</p>
    {hint && <p className="label-caps text-ink-muted mt-3">{hint}</p>}
  </Card>
);

const Overview: React.FC<{ profile: Profile; onNavigate: (tab: TabId) => void }> = ({ profile, onNavigate }) => {
  const isAdmin = profile.role === 'super_admin';
  const [loading, setLoading] = useState(true);
  const [todayCount, setTodayCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [balance, setBalance] = useState(0);
  const [slug, setSlug] = useState('');
  const [payoutRequests, setPayoutRequests] = useState(0);
  const [payoutAmount, setPayoutAmount] = useState(0);
  const [openInvites, setOpenInvites] = useState(0);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      if (isAdmin) {
        const [payData, invData] = await Promise.all([
          apiFetch('/admin/payouts'),
          apiFetch('/admin/invitations'),
        ]);
        const payouts = payData.payouts || [];
        const requested = payouts.filter((p: any) => p.status === 'requested');
        setPayoutRequests(requested.length);
        setPayoutAmount(requested.reduce((acc: number, p: any) => acc + Number(p.amount), 0));
        setOpenInvites((invData.invitations || []).filter((i: any) => i.status === 'pending').length);
      } else {
        const [bookData, balData, profData] = await Promise.all([
          apiFetch('/provider/bookings'),
          apiFetch('/provider/balance'),
          apiFetch('/provider/profile'),
        ]);
        const bookings = bookData.bookings || [];
        const today = new Date().toLocaleDateString('en-CA');
        setTodayCount(bookings.filter((b: any) =>
          new Date(b.start_time).toLocaleDateString('en-CA') === today && b.status !== 'cancelled'
        ).length);
        setPendingCount(bookings.filter((b: any) => b.status === 'pending').length);
        setBalance(Number(balData.balance?.available_balance ?? 0));
        setSlug(profData.profile?.slug || '');
      }
    } catch {
      // Cards degrade to zeros — modules themselves surface errors
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const storefrontUrl = slug ? `${window.location.origin}/#/p/${slug}` : '';

  const copyLink = () => {
    if (!storefrontUrl) return;
    navigator.clipboard.writeText(storefrontUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return (
    <div className="p-12 flex items-center justify-center">
      <p className="label-caps text-ink-faint">Loading overview...</p>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-10 space-y-8">
      <div>
        <h2 className="display text-2xl md:text-3xl text-ink">
          Welcome back{profile.fullName ? `, ${profile.fullName.split(' ')[0]}` : ''}
        </h2>
        <p className="label-caps text-ink-faint mt-2">
          {isAdmin ? 'Platform overview' : 'Your booking business at a glance'}
        </p>
      </div>

      {isAdmin ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          <MetricCard
            label="Payout requests"
            value={String(payoutRequests)}
            hint={payoutRequests > 0 ? `RM ${payoutAmount.toFixed(2)} awaiting review` : 'Nothing awaiting review'}
            onClick={() => onNavigate('payouts')}
          />
          <MetricCard
            label="Open invitations"
            value={String(openInvites)}
            hint={openInvites > 0 ? 'Awaiting acceptance' : 'All invitations accepted'}
            onClick={() => onNavigate('invites')}
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            <MetricCard
              label="Today's bookings"
              value={String(todayCount)}
              onClick={() => onNavigate('bookings')}
            />
            <MetricCard
              label="Pending requests"
              value={String(pendingCount)}
              hint={pendingCount > 0 ? 'Awaiting your confirmation' : undefined}
              onClick={() => onNavigate('bookings')}
            />
            <MetricCard
              label="Available balance"
              value={`RM ${balance.toFixed(2)}`}
              onClick={() => onNavigate('earnings')}
            />
          </div>

          {slug && (
            <div className="bg-inverse text-ink-inverse rounded-sm p-6 border border-line">
              <p className="label-caps text-accent">Your storefront link</p>
              <p className="font-mono text-sm text-slate-300 mt-2 break-all">{storefrontUrl}</p>
              <div className="flex flex-wrap gap-3 mt-4">
                <button
                  onClick={copyLink}
                  className="flex items-center gap-2 bg-accent text-accent-contrast px-5 min-h-[44px] rounded-sm label-caps hover:opacity-90 transition-opacity"
                >
                  {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
                </button>
                <a
                  href={`/#/p/${slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 border border-white/20 text-slate-300 hover:text-white px-5 min-h-[44px] rounded-sm label-caps transition-colors"
                >
                  <ExternalLink size={14} /> Preview
                </a>
              </div>
              <p className="text-slate-400 text-xs mt-4 leading-relaxed">
                Share this link on WhatsApp, Instagram, or anywhere your customers are — they can book and pay without an account.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const Dashboard: React.FC<{ profile: Profile }> = ({ profile }) => {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const navItems = profile.role === 'super_admin' ? ADMIN_NAV : PROVIDER_NAV;

  const handleLogout = () => {
    auth.clearSession();
    window.location.href = window.location.origin;
  };

  const renderContent = () => {
    if (profile.role === 'provider') {
      switch (activeTab) {
        case 'earnings': return <EarningsManagement profile={profile} />;
        case 'bookings': return <BookingManagement profile={profile} />;
        case 'services': return <ServiceCatalog profile={profile} />;
        case 'storefront': return <StorefrontSettings profile={profile} />;
      }
    }

    if (profile.role === 'super_admin') {
      switch (activeTab) {
        case 'invites': return <InvitationManagement profile={profile} />;
        case 'payouts': return <PayoutManagement profile={profile} />;
        case 'settings': return <SystemSettings profile={profile} />;
      }
    }

    return <Overview profile={profile} onNavigate={setActiveTab} />;
  };

  return (
    <div className="min-h-screen bg-surface flex font-sans">
      {/* Desktop sidebar — navy in both modes */}
      <aside
        className={`${isSidebarCollapsed ? 'w-20' : 'w-64'} hidden md:flex bg-inverse flex-col text-ink-inverse transition-all duration-300 ease-in-out overflow-hidden shrink-0`}
      >
        <div className={`p-6 border-b border-white/10 flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} h-20`}>
          <div className="bg-accent text-accent-contrast p-2 rounded-sm shrink-0">
            <Clock size={20} />
          </div>
          {!isSidebarCollapsed && <span className="display text-xl tracking-tight">NexSlot</span>}
        </div>

        <nav className="flex-1 p-4 space-y-1 mt-2">
          {navItems.map(item => (
            <NavButton
              key={item.id}
              item={item}
              active={activeTab === item.id}
              collapsed={isSidebarCollapsed}
              onClick={() => setActiveTab(item.id)}
            />
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            title={isSidebarCollapsed ? 'Sign out' : ''}
            className={`w-full flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-4 px-5'} py-3.5 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-sm label-caps transition-colors`}
          >
            <LogOut size={18} className="shrink-0" />
            {!isSidebarCollapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main column */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-16 md:h-20 bg-raised border-b border-line px-4 md:px-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 md:gap-6 min-w-0">
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="hidden md:flex p-2.5 text-ink-muted hover:text-ink border border-line-strong rounded-sm transition-colors"
              aria-label="Toggle sidebar"
            >
              {isSidebarCollapsed ? <Menu size={18} /> : <ChevronLeft size={18} />}
            </button>
            <span className="md:hidden display text-lg text-ink">NexSlot</span>
            <h2 className="hidden md:block display text-xl text-ink capitalize truncate">
              {(profile.role || 'User').replace('_', ' ')}
            </h2>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            <ThemeToggle />
            <div className="hidden sm:block text-right">
              <p className="text-sm font-semibold text-ink truncate max-w-[160px]">{profile.fullName}</p>
              <p className="text-xs text-ink-faint truncate max-w-[160px]">{profile.email}</p>
            </div>
            <div className="w-10 h-10 bg-inverse text-accent rounded-sm flex items-center justify-center display text-lg shrink-0">
              {profile.fullName?.charAt(0)}
            </div>
            <button
              onClick={handleLogout}
              className="md:hidden p-2.5 text-red-500 border border-line-strong rounded-sm"
              aria-label="Sign out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <div className="overflow-y-auto flex-1 pb-24 md:pb-0">
          {renderContent()}
        </div>
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="fixed bottom-0 inset-x-0 md:hidden bg-inverse border-t border-line flex z-30 pb-[env(safe-area-inset-bottom)]">
        {navItems.map(item => {
          const Icon = item.icon;
          const active = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`flex-1 flex flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] transition-colors ${
                active ? 'text-accent' : 'text-slate-400'
              }`}
            >
              <Icon size={20} />
              <span className="text-[10px] font-medium tracking-wide">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};
